/**
 * "Records of object X changed" — a one-line bus between a record form and any
 * list showing the same object.
 *
 * Before the workspace epic a list owned the form dialog, so it learned about a
 * save from its own `onSaved`. Now the form lives in its own tab and the list
 * stays mounted next to it, so a saved record only appeared after a manual
 * refresh. The form publishes; every list for that object reloads.
 *
 * Delete carries a detail payload so the list can soft-mark / drop a row without
 * clearing the windowed cache (skeleton flicker). Bare notify (save / unknown)
 * still means "refetch everything".
 *
 * Deliberately not an app-wide event bus: keyed by object name so unrelated
 * lists never refetch, and module-level so no context threading is needed.
 */

export type RecordsChangedDetail = {
  reason?: 'save' | 'delete' | 'undelete'
  recordId?: string
  /** Value of `is_deleted` on the row *before* the DELETE (soft escalate). */
  wasMarked?: boolean
}

type Listener = (detail?: RecordsChangedDetail) => void

const listeners = new Map<string, Set<Listener>>()

const keyOf = (objectName: string) => objectName.trim().toLowerCase()

export function notifyRecordsChanged(objectName: string, detail?: RecordsChangedDetail): void {
  const set = listeners.get(keyOf(objectName))
  if (!set) return
  // Copy first — a listener may unsubscribe while we iterate.
  Array.from(set).forEach((fn) => {
    try {
      fn(detail)
    } catch {
      /* one stale subscriber must not block the others */
    }
  })
}

export function subscribeRecordsChanged(objectName: string, fn: Listener): () => void {
  const key = keyOf(objectName)
  let set = listeners.get(key)
  if (!set) {
    set = new Set()
    listeners.set(key, set)
  }
  set.add(fn)
  return () => {
    set!.delete(fn)
    if (set!.size === 0) listeners.delete(key)
  }
}
